import type { ReactNode } from "react";
import { BruneiFlag, CambodiaFlag } from "./flag-art";

/**
 * The eleven SEA Games NOCs' flags, drawn in the app — the same drawings as
 * the portal's results section (`apps/web-portal/src/seagames/runtime/results/results.js`,
 * `FLAGS`), on its 30 × 20 viewBox. `countries.flag_url` is empty in the data,
 * and a venue laptop may be offline, so a flag never depends on fetching an
 * image: a stored `flag_url` still wins (OVR's `Flag`, `NocChip`), these come
 * next, and any other code keeps the neutral swatch.
 *
 * Shared here (no styling, no strings) so OVR and the GMS draw the same
 * flags; each app frames them its own way (OVR `noc-badge.tsx`, GMS
 * `components/common/noc-flag.tsx`).
 *
 * Keyed by the IOC code the API sends as `NocRef.code`; `SIN` is the stored
 * spelling of Singapore (contracts' `RESULTS_FEED_NOC_ALIASES`).
 */

/** A star (or the Malaysian 14-point sun) as polygon points, as the portal draws it. */
function starPoints(cx: number, cy: number, r: number, points = 5, inner = 0.382): string {
	const p: string[] = [];
	for (let i = 0; i < points * 2; i++) {
		const a = -Math.PI / 2 + (i * Math.PI) / points;
		const rr = i % 2 ? r * inner : r;
		p.push(`${(cx + rr * Math.cos(a)).toFixed(2)},${(cy + rr * Math.sin(a)).toFixed(2)}`);
	}
	return p.join(" ");
}

const star = (cx: number, cy: number, r: number, points?: number, inner?: number) => <polygon points={starPoints(cx, cy, r, points, inner)} />;

/** Horizontal bands, top to bottom: [fill, height]. */
function stripes(list: ReadonlyArray<readonly [string, number]>): ReactNode {
	let y = 0;
	return list.map(([fill, h]) => {
		const rect = <rect key={y} y={y} width="30" height={h} fill={fill} />;
		y += h;
		return rect;
	});
}

const MAS_BAND = 20 / 14;

const FLAGS: Record<string, () => ReactNode> = {
	MAS: () => (
		<>
			{Array.from({ length: 14 }, (_, i) => (
				<rect key={i} y={(i * MAS_BAND).toFixed(3)} width="30" height={(MAS_BAND + 0.02).toFixed(3)} fill={i % 2 ? "#fff" : "#cc0001"} />
			))}
			<rect width="15" height={(8 * MAS_BAND).toFixed(2)} fill="#010066" />
			<circle cx="5.6" cy="5.7" r="3.9" fill="#fc0" />
			<circle cx="6.6" cy="5.7" r="3.25" fill="#010066" />
			<g fill="#fc0">{star(10.9, 5.7, 2.6, 14, 0.45)}</g>
		</>
	),
	THA: () =>
		stripes([
			["#a51931", 3.34],
			["#f4f5f8", 3.33],
			["#2d2a4a", 6.66],
			["#f4f5f8", 3.33],
			["#a51931", 3.34],
		]),
	VIE: () => (
		<>
			<rect width="30" height="20" fill="#da251d" />
			<g fill="#ff0">{star(15, 10.4, 6)}</g>
		</>
	),
	INA: () =>
		stripes([
			["#ce1126", 10],
			["#fff", 10],
		]),
	PHI: () => (
		<>
			{stripes([
				["#0038a8", 10],
				["#ce1126", 10],
			])}
			<polygon points="0,0 17.3,10 0,20" fill="#fff" />
			<circle cx="5.6" cy="10" r="2.3" fill="#fcd116" />
			<g fill="#fcd116">
				{star(1.9, 2.6, 0.9)}
				{star(1.9, 17.4, 0.9)}
				{star(14.2, 10, 0.9)}
			</g>
		</>
	),
	SGP: () => (
		<>
			{stripes([
				["#ef3340", 10],
				["#fff", 10],
			])}
			<circle cx="6.4" cy="5" r="3.6" fill="#fff" />
			<circle cx="7.9" cy="5" r="3.3" fill="#ef3340" />
			<g fill="#fff">
				{Array.from({ length: 5 }, (_, i) => {
					const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
					return <polygon key={i} points={starPoints(10.6 + 1.7 * Math.cos(a), 5 + 1.7 * Math.sin(a), 0.62)} />;
				})}
			</g>
		</>
	),
	MYA: () => (
		<>
			{stripes([
				["#fecb00", 6.67],
				["#34b233", 6.66],
				["#ea2839", 6.67],
			])}
			<g fill="#fff">{star(15, 10.9, 7.2, 5, 0.38)}</g>
		</>
	),
	/* Cambodia and Brunei are the official artwork in full (flag-art.tsx) */
	CAM: CambodiaFlag,
	LAO: () => (
		<>
			{stripes([
				["#ce1126", 5],
				["#002868", 10],
				["#ce1126", 5],
			])}
			<circle cx="15" cy="10" r="4" fill="#fff" />
		</>
	),
	BRU: BruneiFlag,
	TLS: () => (
		<>
			<rect width="30" height="20" fill="#dc241f" />
			<polygon points="0,0 15.5,10 0,20" fill="#ffc726" />
			<polygon points="0,0 10.2,10 0,20" fill="#000" />
			<g fill="#fff" transform="rotate(-25 3.8 10)">
				{star(3.8, 10, 2.5)}
			</g>
		</>
	),
};

const ALIASES: Record<string, string> = { SIN: "SGP" };

const flagOf = (code: string | null | undefined) => (code ? FLAGS[ALIASES[code] ?? code] : undefined);

/** Is there a drawn flag for this NOC code? */
export const hasNocFlag = (code: string | null | undefined): boolean => flagOf(code) !== undefined;

/** The drawn flag for an NOC code, filling its box; null for a code with none. Decorative. */
export function NocFlagSvg({ code, className = "" }: { code: string | null | undefined; className?: string }) {
	const draw = flagOf(code);
	if (!draw) return null;
	return (
		<svg viewBox="0 0 30 20" preserveAspectRatio="none" aria-hidden="true" className={className}>
			{draw()}
		</svg>
	);
}
